import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { styled } from 'styled-components';
import type { JSX, PropsWithChildren, ReactNode } from 'react';
import { isNil } from '../../utilities/type-guards';

/* The z-index is required so the tooltip plays nicely with the player and customize */
const TooltipContent = styled(TooltipPrimitive.Content)<{ $maxWidth?: string }>`
  background: ${({ theme }) => theme.color.grey900};
  border-radius: ${({ theme }) => theme.spacing.space01};
  color: white;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.font.weight.regular};
  line-height: 1.5;
  max-width: ${({ $maxWidth }) => $maxWidth ?? 'min(250px, 90vw)'};
  padding: ${({ theme }) => theme.spacing.space03};
  z-index: ${({ theme }) => theme.zIndex.Tooltip};

  a {
    color: white;
  }

  p:first-of-type {
    margin-top: 0;
  }

  p:last-of-type {
    margin-bottom: 0;
  }
`;

const TooltipArrow = styled(TooltipPrimitive.Arrow)`
  fill: ${({ theme }) => theme.color.grey900};
`;

// our Providers.tsx file requires JSX.Elements, not raw ReactNodes. This gets radix's tooltip
// provider to play nicely with our provider factory.
export const TooltipProvider = ({ children }: { children: ReactNode }): JSX.Element => (
  <TooltipPrimitive.Provider>{children}</TooltipPrimitive.Provider>
);

type BasicTooltipProps = PropsWithChildren<{
  content: ReactNode;
  isOpen?: boolean;
  isEnabled?: boolean;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  className?: string;
  disableHoverableContent?: boolean;
  maxWidth?: string;
  delayDuration?: number;
}>;

/**
 * A basic tooltip powered by Radix Tooltip.
 * @see https://www.radix-ui.com/primitives/docs/components/tooltip
 *
 * To modify styles, use the `className` prop to pass in a custom class
 * or override with styled-components.
 *
 * @param content The content to display in the tooltip.
 * @param isOpen Whether the tooltip is open.
 * @param side The side of the tooltip to display.
 * @param disableHoverableContent Whether the content should be hoverable.
 * @param className A custom class name to apply to the tooltip.
 * @param delayDuration The duration of hover on the trigger before tooltip becomes visible.
 * @returns The tooltip component.
 *
 * @example
 * ```tsx
 * cons MyTooltip = styled(BasicTooltip)`
 *  background: red;
 * `;
 *
 * <MyTooltip content="This tooltip is red">
 *  <button>Hover me</button>
 * </MyTooltip>
 * ```
 */
export const BasicTooltip = ({
  content,
  children,
  isOpen,
  side,
  disableHoverableContent,
  className,
  maxWidth,
  delayDuration,
  isEnabled = true,
}: BasicTooltipProps): JSX.Element => {
  if (isNil(content) || !isEnabled) {
    return children as JSX.Element;
  }

  return (
    <TooltipPrimitive.Root
      open={isOpen}
      disableHoverableContent={disableHoverableContent}
      delayDuration={delayDuration}
    >
      <TooltipPrimitive.Trigger asChild={true}>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipContent
          className={className}
          collisionPadding={8}
          sideOffset={4}
          side={side}
          $maxWidth={maxWidth}
        >
          {content}
          <TooltipArrow />
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};
